import React, { useState, useEffect } from 'react';
import { Menu, Save, Eye, EyeOff, Home, Info, Package, Briefcase, Award, Users, MessageSquare, FileText, Shield, Phone, File } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { menuSettingsAPI, pagesAPI } from '../../services/api';

const menuItems = [
  { key: 'home', label: 'Home', icon: Home, description: 'Pagina principale del sito' },
  { key: 'about', label: 'Chi Siamo', icon: Info, description: 'Informazioni sull\'azienda' },
  { key: 'products', label: 'Prodotti & Servizi', icon: Package, description: 'Catalogo prodotti e servizi' },
  { key: 'portfolio', label: 'Portfolio', icon: Briefcase, description: 'Progetti realizzati' },
  { key: 'certifications', label: 'Certificazioni', icon: Award, description: 'Certificazioni aziendali' },
  { key: 'team', label: 'Team', icon: Users, description: 'Il nostro team' },
  { key: 'testimonials', label: 'Testimonianze', icon: MessageSquare, description: 'Feedback dei clienti' },
  { key: 'blog', label: 'News', icon: FileText, description: 'Notizie e articoli' },
  { key: 'compliance', label: 'Compliance', icon: Shield, description: 'Privacy, DPO, Parità di genere' },
  { key: 'contact', label: 'Contatti', icon: Phone, description: 'Pagina contatti' },
];

const MenuSettingsTab = () => {
  const [settings, setSettings] = useState({
    home: true,
    about: true,
    products: true,
    portfolio: true,
    certifications: true,
    team: true,
    testimonials: true,
    blog: true,
    compliance: true,
    contact: true,
    custom_pages: {},
  });
  const [customPages, setCustomPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        menuSettingsAPI.get(),
        pagesAPI.getAll().catch(() => ({ data: [] })),
      ]);
      const data = settingsRes.data;
      const loaded = {
        home: data.home ?? true,
        about: data.about ?? true,
        products: data.products ?? true,
        portfolio: data.portfolio ?? true,
        certifications: data.certifications ?? true,
        team: data.team ?? true,
        testimonials: data.testimonials ?? true,
        blog: data.blog ?? true,
        compliance: data.compliance ?? true,
        contact: data.contact ?? true,
        custom_pages: data.custom_pages || {},
      };
      setSettings(loaded);
      setCustomPages(pagesRes.data);
      setOriginalSettings(loaded);
    } catch (error) {
      console.error('Error loading menu settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
  };

  const handleToggleCustomPage = (slug) => {
    const newCustomPages = { ...settings.custom_pages, [slug]: !settings.custom_pages?.[slug] };
    const newSettings = { ...settings, custom_pages: newCustomPages };
    setSettings(newSettings);
    setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(originalSettings));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await menuSettingsAPI.update(settings);
      setOriginalSettings(settings);
      setHasChanges(false);
      toast.success('Impostazioni menu salvate!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    const allEnabled = { custom_pages: {} };
    menuItems.forEach(item => { allEnabled[item.key] = true; });
    customPages.forEach(page => { allEnabled.custom_pages[page.slug] = true; });
    setSettings(allEnabled);
    setHasChanges(JSON.stringify(allEnabled) !== JSON.stringify(originalSettings));
  };

  const handleDisableAll = () => {
    const allDisabled = { custom_pages: {} };
    menuItems.forEach(item => { allDisabled[item.key] = false; });
    customPages.forEach(page => { allDisabled.custom_pages[page.slug] = false; });
    setSettings(allDisabled);
    setHasChanges(JSON.stringify(allDisabled) !== JSON.stringify(originalSettings));
  };

  const enabledCount = menuItems.filter(item => settings[item.key]).length
    + customPages.filter(page => settings.custom_pages?.[page.slug]).length;
  const totalCount = menuItems.length + customPages.length;

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="py-8 text-center">Caricamento...</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Menu className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Gestione Menu</CardTitle>
                <CardDescription>Abilita o disabilita le voci del menu di navigazione</CardDescription>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-muted-foreground">
                {enabledCount}/{totalCount} voci attive
              </span>
              <Button 
                onClick={handleSave} 
                disabled={saving || !hasChanges}
                data-testid="save-menu-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-end space-x-2 mb-4">
            <Button variant="outline" size="sm" onClick={handleEnableAll}>
              <Eye className="w-4 h-4 mr-2" />
              Abilita Tutti
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisableAll}>
              <EyeOff className="w-4 h-4 mr-2" />
              Disabilita Tutti
            </Button>
          </div>

          <div className="grid gap-3">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isEnabled = settings[item.key];
              
              return (
                <div
                  key={item.key}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    isEnabled 
                      ? 'border-primary/30 bg-primary/5' 
                      : 'border-border bg-muted/30 opacity-60'
                  }`}
                  data-testid={`menu-item-${item.key}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isEnabled ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      <Icon className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${!isEnabled && 'text-muted-foreground'}`}>
                        {item.label}
                      </p>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      isEnabled 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {isEnabled ? 'Visibile' : 'Nascosto'}
                    </span>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => handleToggle(item.key)}
                      data-testid={`toggle-${item.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Custom Pages Section */}
          <div className="mt-8">
            <h3 className="text-sm font-medium mb-3 text-muted-foreground">Pagine Personalizzate</h3>
            {customPages.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 rounded-lg border border-dashed border-border" data-testid="no-custom-pages-message">
                Nessuna pagina personalizzata creata. Creale nella sezione "Pagine" del pannello admin.
              </p>
            ) : (
              <div className="grid gap-3">
                {customPages.map((page) => {
                  const isEnabled = !!settings.custom_pages?.[page.slug];
                  return (
                    <div
                      key={page.slug}
                      className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                        isEnabled
                          ? 'border-primary/30 bg-primary/5'
                          : 'border-border bg-muted/30 opacity-60'
                      }`}
                      data-testid={`menu-item-custom-${page.slug}`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isEnabled ? 'bg-primary/20' : 'bg-muted'
                        }`}>
                          <File className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${!isEnabled && 'text-muted-foreground'}`}>
                            {page.title}
                          </p>
                          <p className="text-sm text-muted-foreground font-mono">/{page.slug}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isEnabled
                            ? 'bg-green-500/20 text-green-500'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {isEnabled ? 'Visibile' : 'Nascosto'}
                        </span>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggleCustomPage(page.slug)}
                          data-testid={`toggle-custom-${page.slug}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="glass-card bg-blue-500/5 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-500">Nota</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Le voci del menu disabilitate non saranno visibili ai visitatori del sito, ma rimarranno accessibili dal pannello admin.
                Le pagine non verranno eliminate, solo nascoste dalla navigazione pubblica.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MenuSettingsTab;
